import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginType, setLoginType] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://admin-golang-backend-8cdf9b481f22.herokuapp.com';

  // FIX: Wrapped in useCallback to be used as dependency
  const getBaseHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  }), []);

  // FIX: Moved up and wrapped in useCallback so checkAuthStatus can use it
  const clearAuthData = useCallback(() => {
    localStorage.removeItem('jwt');
    localStorage.removeItem('user');
    localStorage.removeItem('loginType');
    localStorage.removeItem('adminId');
    setUser(null);
    setLoginType(null);
    setIsAuthenticated(false);
    console.log('Auth data cleared');
  }, []);

  // FIX: Wrapped in useCallback to fix useEffect dependency warning
  const checkAuthStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('jwt');
      const storedUser = localStorage.getItem('user');
      const storedLoginType = localStorage.getItem('loginType');
      
      // FIX: Removed unused 'storedAdminId' variable to fix no-unused-vars error

      if (token && storedUser && storedLoginType) {
        // Parse user immediately to ensure UI has data while validating in background
        const parsedUser = JSON.parse(storedUser);
        
        if (storedLoginType === 'master') {
          setUser(parsedUser);
          setLoginType(storedLoginType);
          setIsAuthenticated(true);
          setLoading(false);
          return;
        }
        
        // Use Authorization Bearer header for validation
        const response = await fetch(`${API_BASE_URL}/auth/validate`, {
          method: 'POST',
          headers: { 
            ...getBaseHeaders(), 
            'Authorization': `Bearer ${token}` 
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.valid) {
            // Update user state
            setUser(parsedUser);
            setLoginType(storedLoginType);
            setIsAuthenticated(true);
          } else {
            console.warn('Token validation failed on server');
            clearAuthData();
          }
        } else {
          console.warn('Token validation response not OK');
          clearAuthData();
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Auth validation error:', error);
      clearAuthData();
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, getBaseHeaders, clearAuthData]); // Added dependencies

  // FIX: Added checkAuthStatus to dependency array
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Storage event listeners for tab syncing
  // FIX: Added checkAuthStatus and clearAuthData to dependency array
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'jwt') {
        if (!event.newValue) {
          console.log('JWT was removed from storage. Logging out this tab to sync state.');
          clearAuthData();
        } else {
          console.log('JWT was updated in another tab. Re-validating session.');
          checkAuthStatus();
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkAuthStatus, clearAuthData]);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: getBaseHeaders(),
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        const userData = {
          adminId: data.admin_id,
          email: data.login_type === 'master' ? 'Master User' : email,
          sessionId: data.session_id,
          expiresAt: data.expires_at,
          loginType: data.login_type,
          ipAddress: data.ip_address,
        };
        
        // Store data
        localStorage.setItem('jwt', data.jwt);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('loginType', data.login_type);
        localStorage.setItem('adminId', data.admin_id);
        
        // Update State
        setUser(userData);
        setLoginType(data.login_type);
        setIsAuthenticated(true);
        
        return { success: true, message: data.message, loginType: data.login_type };
      } else {
        if (data && data.status) {
          const statusMessage = `Su cuenta está ${data.status}. Por favor, contacte a un administrador.`;
          return { success: false, message: statusMessage, status: data.status };
        }
        return { success: false, message: data.error || data.message || 'Error de inicio de sesión' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Error de conexión. Por favor intente nuevamente.' };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('jwt');
      const adminId = localStorage.getItem('adminId');
      const userStr = localStorage.getItem('user');
      if (token && adminId && userStr) {
        const userData = JSON.parse(userStr);
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: { 
            ...getBaseHeaders(), 
            'Authorization': `Bearer ${token}` 
          },
          body: JSON.stringify({ admin_id: adminId, session_id: userData.sessionId || 0 }),
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthData();
    }
  };

  const refreshToken = async () => {
    try {
      const token = localStorage.getItem('jwt');
      if (!token) return false;
      
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 
          ...getBaseHeaders(), 
          'Authorization': `Bearer ${token}` 
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          localStorage.setItem('jwt', data.jwt);
          const userData = {
            adminId: data.admin_id,
            email: data.login_type === 'master' ? 'Master User' : user?.email || '',
            sessionId: data.session_id,
            expiresAt: data.expires_at,
            loginType: data.login_type || user?.loginType,
            ipAddress: data.ip_address,
          };
          localStorage.setItem('user', JSON.stringify(userData));
          if (data.login_type) {
            localStorage.setItem('loginType', data.login_type);
          }
          setUser(userData);
          console.log('Token refreshed successfully');
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('jwt');
    const adminId = localStorage.getItem('adminId');
    const storedLoginType = localStorage.getItem('loginType');
    
    if (!token) return getBaseHeaders();

    const headers = { 
      ...getBaseHeaders(), 
      'Authorization': `Bearer ${token}` 
    };

    if (storedLoginType === 'master') {
      const masterSecret = process.env.REACT_APP_MASTER_SECRET;
      if (!masterSecret) {
        // console.warn('REACT_APP_MASTER_SECRET not configured!');
      }
      if (masterSecret) headers['secret_credential'] = masterSecret;
    } else if (storedLoginType === 'admin') {
      if (adminId) {
        headers['admin_id'] = adminId;
      }
    }
    return headers;
  };

const makeAuthenticatedRequest = async (url, options = {}) => {
    try {
      // 1. Merge default headers with custom options
      const headers = { ...getAuthHeaders(), ...options.headers };

      // 2. CRITICAL FIX: If body is FormData, remove Content-Type.
      // This allows the browser to automatically set 'multipart/form-data; boundary=...'
      if (options.body instanceof FormData) {
        delete headers['Content-Type'];
      }
      
      // 3. Also remove if explicitly set to undefined (cleanup)
      if (headers['Content-Type'] === undefined) {
         delete headers['Content-Type'];
      }

      // 4. Perform the fetch with the cleaned headers
      const response = await fetch(url, {
        ...options,
        headers, 
      });

      if (response.status === 401) {
        console.log('Received 401, attempting token refresh');
        const refreshed = await refreshToken();
        if (refreshed) {
          console.log('Token refreshed, retrying request');
          
          // Re-calculate headers for the retry
          const retryHeaders = { ...getAuthHeaders(), ...options.headers };
          if (options.body instanceof FormData || retryHeaders['Content-Type'] === undefined) {
            delete retryHeaders['Content-Type'];
          }

          return fetch(url, {
            ...options,
            headers: retryHeaders,
          });
        }
        console.log('Token refresh failed, logging out');
        logout();
        throw new Error('Session expired');
      }
      return response;
    } catch (error) {
      console.error('Authenticated request failed:', error);
      throw error;
    }
  };

  const hasAdminAccess = () => loginType === 'admin' || loginType === 'master';
  const hasMasterAccess = () => loginType === 'master';
  
  const getAuthInfo = () => ({
    isAuthenticated, loginType, user, hasAdminAccess: hasAdminAccess(), hasMasterAccess: hasMasterAccess(),
  });

  const value = {
    isAuthenticated, 
    user, 
    loginType, 
    loading, 
    
    // Explicitly expose adminId. Fallback to localStorage if state isn't ready.
    adminId: user?.adminId || localStorage.getItem('adminId'),
    
    login, 
    logout, 
    refreshToken, 
    getAuthHeaders,
    makeAuthenticatedRequest, 
    clearAuthData, 
    hasAdminAccess, 
    hasMasterAccess, 
    getAuthInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};